import { useEffect, useState, useContext } from "react";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import StorefrontIcon from "@mui/icons-material/Storefront";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import toast from "react-hot-toast";

import InputText from "../../../shared/components/inputs/InputText/InputText";
import { AuthContext } from "../../../shared/context/auth-context";
import { useLoading } from "../../../shared/context/LoadingContext";
import "../categoriasFinanceiro/CategoriasFinanceiro.scss";

const VAZIO = {
  _id: null,
  nome: "",
  codigo: "",
  ativo: true,
  endereco: "",
  cidade: "",
  ilha: "",
  codigoPostal: "",
  contacto: "",
  contacto2: "",
  email: "",
  led: "",
  serie: "",
  cliente_id: "",
  cliente_oauth_secret: "",
};

/**
 * Unidades (estabelecimentos) da clínica.
 *
 * Até aqui só se criavam por script e editavam-se direto na base de dados.
 *
 * O que uma unidade decide: a morada impressa nos documentos, o LED por onde
 * saem os documentos eletrónicos, e a sequência de numeração — que é própria de
 * cada uma. Daí o LED ter de ser único: duas unidades no mesmo LED partilhariam
 * a numeração e a e-fatura rejeitaria os números repetidos.
 *
 * A unidade PRINCIPAL não se edita nem se remove: é o destino por omissão de
 * tudo o que não tem unidade atribuída.
 */
function Unidades({ sendRequest, clinicaId }) {
  const { startLoading, stopLoading } = useLoading();
  const auth = useContext(AuthContext);

  const [unidades, setUnidades] = useState([]);
  const [modal, setModal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const carregar = async () => {
      startLoading();
      try {
        // `?todas=true`: ecrã de gestão — mostra também as inativas e as que
        // não estão associadas a quem está a ver.
        const resposta = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinicaId}?todas=true`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setUnidades(resposta.estabelecimentos || []);
      } catch (err) {
        console.error("err", err);
      } finally {
        stopLoading();
      }
    };
    carregar();
  }, [sendRequest, clinicaId, auth.token]);

  const abrirNova = () => setModal({ ...VAZIO });

  const abrirEdicao = (u) =>
    setModal({
      ...VAZIO,
      _id: u._id,
      nome: u.nome || "",
      codigo: u.codigo || "",
      ativo: u.ativo !== false,
      endereco: u.endereco || "",
      cidade: u.cidade || "",
      ilha: u.ilha || "",
      codigoPostal: u.codigoPostal || "",
      contacto: u.contacto || "",
      contacto2: u.contacto2 || "",
      email: u.email || "",
      led: u.dadosEmissor?.led || "",
      serie: u.dadosEmissor?.serie || "",
      // As credenciais nunca são devolvidas pelo servidor. Em branco significam
      // «manter as que lá estão».
      cliente_id: "",
      cliente_oauth_secret: "",
    });

  const campo = (k) => (valor) => setModal((m) => ({ ...m, [k]: valor }));

  const guardar = async () => {
    if (!modal.nome.trim() || !modal.codigo.trim()) {
      toast.error("O nome e o código são obrigatórios.");
      return;
    }

    setIsSubmitting(true);
    startLoading();
    try {
      const corpo = {
        nome: modal.nome,
        codigo: modal.codigo,
        endereco: modal.endereco,
        cidade: modal.cidade,
        ilha: modal.ilha,
        codigoPostal: modal.codigoPostal,
        contacto: modal.contacto,
        contacto2: modal.contacto2,
        email: modal.email,
        led: modal.led,
        serie: modal.serie,
        // Só vão quando preenchidas — em branco quer dizer «não mexer».
        ...(modal.cliente_id && { cliente_id: modal.cliente_id }),
        ...(modal.cliente_oauth_secret && {
          cliente_oauth_secret: modal.cliente_oauth_secret,
        }),
      };

      if (modal._id) {
        const resposta = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/${modal._id}`,
          "PATCH",
          JSON.stringify({ ...corpo, ativo: modal.ativo }),
          {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          },
        );
        setUnidades((prev) =>
          prev.map((u) =>
            u._id === modal._id ? resposta.estabelecimento : u,
          ),
        );
        toast.success("Unidade atualizada com sucesso");
      } else {
        const resposta = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinicaId}`,
          "POST",
          JSON.stringify(corpo),
          {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          },
        );
        setUnidades((prev) => [...prev, resposta.estabelecimento]);
        toast.success(
          "Unidade criada. Os utilizadores passam a escolher a unidade ao entrar.",
        );
      }
      setModal(null);
    } catch (err) {
      console.error("err", err);
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const podeGerir =
    auth.role === "Médico/Administrador" || auth.role === "Gerente";

  return (
    <>
      <div className="categoriasGeral__container">
        {podeGerir && (
          <div className="categoriasGeral__container__btn-adicionar">
            <Button variant="contained" color="success" onClick={abrirNova}>
              Nova Unidade
            </Button>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #90caf9",
            borderRadius: "8px",
            padding: "16px 20px",
            margin: "16px 0",
            display: "flex",
            gap: "12px",
          }}
        >
          <InfoOutlinedIcon style={{ color: "#1565c0" }} />
          <div style={{ fontSize: "14px", color: "#0d47a1" }}>
            <strong style={{ display: "block", marginBottom: "6px" }}>
              Como funcionam as Unidades?
            </strong>
            <ul style={{ margin: 0, paddingLeft: "18px", lineHeight: 1.6 }}>
              <li>
                Uma unidade é um local físico da clínica. A morada e os
                contactos dela são os que saem impressos nos documentos emitidos
                lá.
              </li>
              <li>
                Cada unidade emite documentos eletrónicos pela <b>sua</b> LED,
                com numeração independente. Duas unidades não podem partilhar a
                mesma LED.
              </li>
              <li>
                A partir da segunda unidade, os utilizadores passam a escolher
                onde estão a trabalhar ao iniciar sessão.
              </li>
              <li>
                A unidade <b>principal</b> é o destino por omissão de tudo o que
                não tem unidade atribuída, e não pode ser removida.
              </li>
            </ul>
          </div>
        </div>

        <div className="categoriasGeral__container__lista">
          {unidades.map((u) => (
            <div
              key={u._id}
              className="categoriasGeral__container__lista__item"
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "12px",
                opacity: u.ativo === false ? 0.6 : 1,
              }}
            >
              <div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <StorefrontIcon style={{ fontSize: 18, color: "#666" }} />
                  <strong>{u.nome}</strong>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "1px 7px",
                      borderRadius: "999px",
                      color: "#1d4ed8",
                      backgroundColor: "rgba(29,78,216,0.1)",
                    }}
                  >
                    {u.codigo}
                  </span>
                  {u.principal && (
                    <span style={{ fontSize: "11px", color: "#2e7d32" }}>
                      Principal
                    </span>
                  )}
                  {u.ativo === false && (
                    <span style={{ fontSize: "11px", color: "#b71c1c" }}>
                      Inativa
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {u.endereco || "Sem morada"}
                  {u.cidade ? ` — ${u.cidade}` : ""}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {u.dadosEmissor?.led
                    ? `LED ${u.dadosEmissor.led}${
                        u.dadosEmissor?.serie
                          ? ` · Série ${u.dadosEmissor.serie}`
                          : ""
                      }`
                    : "Sem LED — não pode emitir documentos eletrónicos"}
                </div>
              </div>

              {podeGerir && (
                <Tooltip title="Editar unidade">
                  <EditIcon
                    style={{ cursor: "pointer", color: "#0c53fb" }}
                    onClick={() => abrirEdicao(u)}
                  />
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}>
        <Box
          className="fluxo-atendimento__modal__box credencial__modal"
          id="modal__credencial"
          style={{ maxHeight: "85vh", overflowY: "auto" }}
        >
          <h1 className="fluxo-atendimento__modal__titulo">
            {modal?._id ? "Editar Unidade" : "Nova Unidade"}
          </h1>

          {modal && (
            <div className="modalAdicionarPerfil__container">
              <InputText
                label="Nome *"
                initialValue={modal.nome}
                handleChange={campo("nome")}
              />
              <InputText
                label="Código *"
                initialValue={modal.codigo}
                handleChange={campo("codigo")}
              />
              <InputText
                label="Morada"
                initialValue={modal.endereco}
                handleChange={campo("endereco")}
              />
              <InputText
                label="Cidade"
                initialValue={modal.cidade}
                handleChange={campo("cidade")}
              />
              <InputText
                label="Ilha"
                initialValue={modal.ilha}
                handleChange={campo("ilha")}
              />
              <InputText
                label="Código Postal"
                initialValue={modal.codigoPostal}
                handleChange={campo("codigoPostal")}
              />
              <InputText
                label="Email"
                initialValue={modal.email}
                handleChange={campo("email")}
              />
              <InputText
                label="Contacto"
                initialValue={modal.contacto}
                handleChange={campo("contacto")}
              />
              <InputText
                label="Contacto 2"
                initialValue={modal.contacto2}
                handleChange={campo("contacto2")}
              />

              <p style={{ fontSize: "13px", color: "#666", margin: "12px 0 4px" }}>
                <b>Identidade fiscal.</b> A LED tem de ser diferente da das
                outras unidades: é ela que separa a numeração dos documentos
                eletrónicos. Sem LED, a unidade não pode emitir.
              </p>

              <InputText
                label="LED"
                initialValue={modal.led}
                handleChange={campo("led")}
              />
              <InputText
                label="Série"
                initialValue={modal.serie}
                handleChange={campo("serie")}
              />
              <InputText
                label={
                  modal._id
                    ? "Cliente ID (deixe em branco para manter)"
                    : "Cliente ID (vazio herda da clínica)"
                }
                initialValue={modal.cliente_id}
                handleChange={campo("cliente_id")}
              />
              <InputText
                label={
                  modal._id
                    ? "Cliente Secret (deixe em branco para manter)"
                    : "Cliente Secret (vazio herda da clínica)"
                }
                initialValue={modal.cliente_oauth_secret}
                handleChange={campo("cliente_oauth_secret")}
              />

              {modal._id && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "12px",
                    fontSize: "14px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={modal.ativo}
                    onChange={(e) =>
                      setModal((m) => ({ ...m, ativo: e.target.checked }))
                    }
                  />
                  Unidade ativa
                </label>
              )}
            </div>
          )}

          <div className="fluxo-atendimento__modal__bottom_btns">
            <span
              className="cancel-btn"
              onClick={() => setModal(null)}
              style={{ display: "block" }}
            >
              Cancelar
            </span>
            <span
              className="blue-button"
              disabled={isSubmitting}
              onClick={guardar}
              style={{ display: "block" }}
            >
              {isSubmitting ? "A guardar..." : "Guardar"}
            </span>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default Unidades;
